import { FOOTER } from "@/constants/testIds";
import { LOGO_URL, AREAS } from "@/data/brand";
import { ShieldCheck, Heart } from "lucide-react";
import { WhatsAppIcon, whatsappUrl, formatWhatsAppNumber } from "@/lib/whatsapp";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid={FOOTER.root}
      className="relative bg-black text-white"
    >
      <div className="hi-vis-stripe h-3 w-full" />
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={LOGO_URL}
                alt="Kay's Waste Management"
                className="h-14 w-14 rounded-full border-2 border-white bg-black object-cover"
              />
              <div>
                <div className="font-script text-2xl text-[var(--kay-pink)]">
                  Kay's
                </div>
                <div className="font-display text-sm uppercase tracking-widest">
                  Waste Management
                </div>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-neutral-300">
              Woman-founded, family-owned waste clearance serving Somerset,
              Bristol and beyond. Cheaper than a skip, friendlier by miles.
            </p>

            <div
              data-testid={FOOTER.licensedBadge}
              className="mt-6 inline-flex items-center gap-2 border-2 border-white bg-[var(--kay-pink)] px-3 py-2 text-xs font-bold uppercase tracking-widest text-black"
            >
              <ShieldCheck size={14} />
              Upper Tier Licensed Waste Carrier
            </div>

            <div className="mt-6">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--kay-pink)]">
                Chat on WhatsApp
              </div>
              <a
                href={whatsappUrl("Hi Kay! I'd like to enquire about a waste collection.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="mt-3 inline-flex items-center gap-3 border-2 border-white bg-[#25D366] px-4 py-3 font-display text-sm uppercase tracking-widest text-black hard-shadow-sm hover-lift"
              >
                <WhatsAppIcon size={18} />
                {formatWhatsAppNumber()}
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--kay-pink)]">
              Explore
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              {[
                ["Why Us", "#why"],
                ["Services", "#services"],
                ["Areas", "#areas"],
                ["Pricing", "#pricing"],
                ["About", "#about"],
                ["FAQ", "#faq"],
                ["Contact", "/contact"],
              ].map(([label, href]) => (
                <li key={label}>
                  <a
                    href={href}
                    className="text-neutral-300 transition-colors hover:text-white"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--kay-pink)]">
              Areas Covered
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {AREAS.map((a) => (
                <span
                  key={a}
                  className="border border-white/30 px-2 py-1 text-xs text-neutral-300"
                >
                  {a}
                </span>
              ))}
            </div>
            <p className="mt-4 text-xs text-neutral-400">
              …and all surrounding areas
            </p>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-6 border-t border-white/20 pt-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs font-bold uppercase tracking-widest text-neutral-400">
              We accept
            </span>
            <div className="flex items-center gap-3">
              {[
                { name: "Visa", src: "https://cdn.simpleicons.org/visa/ffffff" },
                { name: "Mastercard", src: "https://cdn.simpleicons.org/mastercard/ffffff" },
                { name: "Apple Pay", src: "https://cdn.simpleicons.org/applepay/ffffff" },
                { name: "Google Pay", src: "https://cdn.simpleicons.org/googlepay/ffffff" },
              ].map((p) => (
                <div
                  key={p.name}
                  data-testid={`pay-${p.name.toLowerCase().replaceAll(" ", "-")}`}
                  className="flex h-12 w-20 items-center justify-center border-2 border-white/40 bg-white/10 px-3 py-2"
                  title={p.name}
                >
                  <img
                    src={p.src}
                    alt={p.name}
                    className="h-7 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-start gap-2 text-sm text-neutral-400 md:items-end">
            <div>© {year} Kay's Waste Management. All rights reserved.</div>
            <div className="flex items-center gap-1">
              Made with <Heart size={14} className="text-[var(--kay-pink)]" fill="currentColor" strokeWidth={0} /> locally
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
